import { useTranslation } from 'react-i18next';

import { useIsRestrictedBySiteSpecificRole } from 'utils/customHooks/useIsRestrictedBySiteSpecificRole';
import { useShowSitePartitioningItems } from 'utils/customHooks/useShowSitePartitioningItems';

import { OpDataFetchSelect } from '../OpDataFetchSelect/OpDataFetchSelect';
import { OpTooltip } from '../OpTooltip/OpTooltip';

import { OpSiteSpecificGroupDataFetchSelectProps } from './types';

/**
 * Component that wraps `OpDataFetchSelect` to fetch and display groups that would
 * normally not have data due to site-specific access limitations.
 *
 * @param {OpSiteSpecificGroupDataFetchSelectProps} props - The properties for the component.
 * @param {string} props.orgId - The organization ID used to fetch group data.
 * @param {object} props.opDataFetchSelectProps - Additional properties passed to `OpDataFetchSelect`.
 *
 * @returns {JSX.Element} The rendered `OpSiteSpecificGroupDataFetchSelect` component.
 *
 * @remarks
 * It uses the `useIsRestrictedBySiteSpecificRole` hook to determine if the group has
 * site-specific restrictions and modifies the options and labels accordingly.
 * The `createDisabledOption` function disables options for groups restricted by site-specific roles
 * the `createOptionLabel` function displays a tooltip with a restriction message if the group is restricted.
 */
export const OpSiteSpecificGroupDataFetchSelect = ({
  orgId,
  ...opDataFetchSelectProps
}: OpSiteSpecificGroupDataFetchSelectProps) => {
  const { t } = useTranslation();
  const checkSiteSpecificRoleRestriction =
    useIsRestrictedBySiteSpecificRole(orgId);
  const { isShowingSitePartitioningItems } =
    useShowSitePartitioningItems(orgId);

  return (
    <OpDataFetchSelect
      queryOptions={{
        apiEndpointName: 'listGroups',
        parameters: isShowingSitePartitioningItems
          ? [orgId, { options: 'withSites' }]
          : [orgId],
      }}
      fetchAllInitialValues={{
        /**
         * Pass withSites and ignoreSiteSpecificAccess to fetch all groups so that we can create options for
         * any groups that may have been restricted by site-specific access. Creating the options
         * allows us to display the group name as well as disabling the option if the group has
         * site-specific restrictions.
         */
        ...(isShowingSitePartitioningItems && {
          queryParamOverrides: {
            options: 'withSites,ignoreSiteSpecificAccess',
          },
        }),
      }}
      createDisabledOption={({ sites }) =>
        checkSiteSpecificRoleRestriction(sites?.map(({ id }) => id))
      }
      // eslint-disable-next-line react/no-unstable-nested-components
      createOptionLabel={({ name, sites: groupSites }) => (
        <OpTooltip
          title={
            checkSiteSpecificRoleRestriction(
              groupSites?.map(({ id: siteId }) => siteId),
            )
              ? t('Your site-specific role prevents management of this group')
              : null
          }
        >
          {name}
        </OpTooltip>
      )}
      {...opDataFetchSelectProps}
    />
  );
};
